import { hasSpecialChar, hasUpperLetter, minLength, ROUTE_PATHS } from '@/constants';
import { Box, Button, Container, Flex, useDisclosure } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { Section } from '../components/Section';
import { PlainLayout } from '../layout';
import { Auth } from '@aws-amplify/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { hasEmptyFields } from '@/utils';
import { PasswordFeedback, PasswordInput } from '@/components/shared';
import { useForm } from 'react-hook-form';

interface ISimpleSignUpFormProps {
  onSubmit?: (data: { password: string }) => void;
}

export const PasswordResetForm: React.FC<ISimpleSignUpFormProps> = (): JSX.Element => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const [searchParams] = useSearchParams();
  const confirmCode: string | null = searchParams.get('code');
  const username: string | null = searchParams.get('email');

  const {
    formState: { errors },
    getValues,
    handleSubmit: submitHandler,
    register,
    watch,
  } = useForm<{ password: string }>({ mode: 'all', criteriaMode: 'all' });

  const areRequiredFieldsEmpty = hasEmptyFields(watch());

  const isDisabled = !!errors.password?.types || areRequiredFieldsEmpty;

  const goToPage = (replace = false, path: string): void => {
    navigate(path, { replace });
  };

  const handleLoginFormSubmit = ({ password }: { password: string }): void => {
    if (username && confirmCode)
      Auth.forgotPasswordSubmit(username, confirmCode, password)
        .then((data) => console.log(data))
        .catch((error) => console.log(error));

    const path = `/${ROUTE_PATHS.passwordRecoveryConfirmation}`;
    goToPage(true, path);
  };

  return (
    <PlainLayout>
      <Box bg="brand.sand" px={8} py={{ base: 8, md: 16 }} w="full">
        <Container bg="white" minW="75%" px={{ base: '5%', md: '22%' }} py="5%">
          <Section
            containerProps={{ py: 0 }}
            headingProps={{ mx: 'auto' }}
            id="login-form"
            title={t('LOGIN.PASSWORD_RESET')}
          >
            <form
              autoComplete="off"
              id="loginForm"
              name="loginForm"
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={submitHandler(handleLoginFormSubmit)}
            >
              <Flex direction="column" gap={8} mb={8}>
                <PasswordFeedback
                  error={errors.password}
                  isOpen={isOpen}
                  value={getValues('password')}
                  onClose={onClose}
                >
                  <PasswordInput
                    id="password"
                    placeholder={t('FORM.NEW_PASSWORD')}
                    {...register('password', {
                      required: true,
                      validate: {
                        minLength: (value) => minLength(value, 8),
                        hasUpperLetter,
                        hasSpecialChar,
                      },
                      onBlur: onClose,
                      onChange: () => {
                        if (!isOpen) onOpen();
                      },
                    })}
                  />
                </PasswordFeedback>
                <Button disabled={isDisabled} type="submit" variant="mbePrimary">
                  {t('ACTIONS.CONFIRM')}
                </Button>
              </Flex>
            </form>
          </Section>
        </Container>
      </Box>
    </PlainLayout>
  );
};
